import { getVatSettlementLabel } from './vatLabels';

export type ParsedVoiceCommand =
  | {
      status: 'parsed';
      action: 'update_area';
      areaM2: number;
      label: string;
    }
  | {
      status: 'parsed';
      action: 'add_line_item';
      itemName: string;
      quantity?: number;
      unit?: string;
      label: string;
    }
  | {
      status: 'parsed';
      action: 'add_material_item';
      itemName: string;
      quantity?: number;
      unit?: string;
      label: string;
    }
  | {
      status: 'parsed';
      action: 'toggle_priming';
      enabled: true;
      label: string;
    }
  | {
      status: 'parsed';
      action: 'set_vat';
      settlementMode: 'no_vat' | 'vat_8' | 'vat_23';
      label: string;
    }
  | {
      status: 'parsed';
      action: 'set_material_mode';
      materialMode: 'CLIENT_MATERIALS' | 'CONTRACTOR_MATERIALS' | 'MIXED_MATERIALS';
      label: string;
    }
  | {
      status: 'uncertain';
      message: string;
    };

const UNCERTAIN_MESSAGE = 'Nie jestem pewien. Popraw tekst ręcznie.';

export function parseVoiceCommand(input: string): ParsedVoiceCommand {
  const original = input.trim();
  const text = normalizeText(original);

  if (!text) {
    return { status: 'uncertain', message: UNCERTAIN_MESSAGE };
  }

  const vat = parseVat(text);
  if (vat) return vat;

  const materialMode = parseMaterialMode(text);
  if (materialMode) return materialMode;

  if (hasAny(text, ['dodaj gruntowanie', 'dopisz gruntowanie', 'gruntowanie', 'dodaj grunt'])) {
    return {
      status: 'parsed',
      action: 'toggle_priming',
      enabled: true,
      label: 'Dodaj gruntowanie',
    };
  }

  const area = parseArea(text);
  if (area) return area;

  const material = parseMaterialItem(original, text);
  if (material) return material;

  const lineItem = parseLineItem(original, text);
  if (lineItem) return lineItem;

  return { status: 'uncertain', message: UNCERTAIN_MESSAGE };
}

export function formatVoiceCommandPreview(command: ParsedVoiceCommand): string {
  if (command.status === 'uncertain') {
    return command.message;
  }

  if (command.action === 'update_area') {
    return `Akcja: ustawienie powierzchni podłogi na ${command.areaM2} m².`;
  }

  if (command.action === 'toggle_priming') {
    return 'Akcja: dodanie gruntowania do zakresu.';
  }

  if (command.action === 'set_vat') {
    return `Akcja: ustawienie rozliczenia ${command.label}.`;
  }

  if (command.action === 'set_material_mode') {
    return `Akcja: ustawienie materiałów: ${command.label}.`;
  }

  const quantity = command.quantity && command.unit ? `${command.quantity} ${command.unit}` : 'ilość do sprawdzenia';
  return `Akcja: dopisanie "${command.itemName}", ${quantity}.`;
}

function parseArea(text: string): ParsedVoiceCommand | null {
  const hasAreaContext = hasFloorAreaContext(text);
  if (!hasAreaContext) return null;

  const isExplicitUpdate = hasAny(text, ['zmien', 'ustaw', 'popraw', 'na']);
  if (!isExplicitUpdate && !isNaturalFloorAreaPhrase(text)) return null;

  const value = parseFloorAreaM2(text);
  if (!value) return null;

  return {
    status: 'parsed',
    action: 'update_area',
    areaM2: value,
    label: `Powierzchnia podłogi ${value} m²`,
  };
}

export function parseFloorAreaM2(input: string): number | undefined {
  const text = normalizeText(input).replace(/m²/g, 'm2');
  if (!hasFloorAreaContext(text)) return undefined;

  const value = parseFirstNumber(text);
  return value && value > 0 ? value : undefined;
}

function hasFloorAreaContext(text: string): boolean {
  return hasAny(text, ['metraz', 'powierzchnia', 'metry', 'm2', 'podloga', 'podlogi', 'po podlodze', 'pokoj']);
}

function isNaturalFloorAreaPhrase(text: string): boolean {
  return (
    text.includes('m2') ||
    hasAny(text, [
      'pow po podlodze',
      'pow. podlogi',
      'pow podlogi',
      'powierzchnia podlogi',
      'podloga',
      'po podlodze',
      'pokoj',
    ])
  );
}

/**
 * Contractors say "vat", "FV" and "faktura" for the same thing, so all three are
 * accepted on the way in. What comes back out is always the VAT wording — "FV"
 * names an invoice type, not the rate the price carries.
 */
function parseVat(text: string): ParsedVoiceCommand | null {
  const mentionsVat = text.includes('vat') || text.includes('faktur') || /\bfv\b/.test(text);
  if (!mentionsVat) return null;

  if (text.includes('23')) {
    return {
      status: 'parsed',
      action: 'set_vat',
      settlementMode: 'vat_23',
      label: getVatSettlementLabel('vat_23'),
    };
  }

  if (text.includes('8') || text.includes('osiem')) {
    return {
      status: 'parsed',
      action: 'set_vat',
      settlementMode: 'vat_8',
      label: getVatSettlementLabel('vat_8'),
    };
  }

  if (hasAny(text, ['bez vat', 'ryczalt', 'zero'])) {
    return { status: 'parsed', action: 'set_vat', settlementMode: 'no_vat', label: 'Ryczałt / bez VAT' };
  }

  return null;
}

function parseMaterialMode(text: string): ParsedVoiceCommand | null {
  if (!text.includes('material')) return null;

  if (hasAny(text, ['po stronie klienta', 'klienta'])) {
    return {
      status: 'parsed',
      action: 'set_material_mode',
      materialMode: 'CLIENT_MATERIALS',
      label: 'materiał po stronie klienta',
    };
  }

  if (hasAny(text, ['po stronie wykonawcy', 'wykonawca', 'robocizna i material', 'robocizna plus material'])) {
    return {
      status: 'parsed',
      action: 'set_material_mode',
      materialMode: 'CONTRACTOR_MATERIALS',
      label: 'materiał po stronie wykonawcy',
    };
  }

  if (hasAny(text, ['mieszane', 'czesc klient', 'czesc wykonaw'])) {
    return {
      status: 'parsed',
      action: 'set_material_mode',
      materialMode: 'MIXED_MATERIALS',
      label: 'materiały mieszane',
    };
  }

  return null;
}

function parseMaterialItem(original: string, text: string): ParsedVoiceCommand | null {
  if (!hasAny(text, ['dodaj', 'dopisz'])) return null;

  const materialName = findFirst(text, ['akryl', 'grunt', 'tasma', 'tasmy', 'folia', 'folie', 'walki', 'pedzle', 'chemia']);
  if (!materialName) return null;

  const quantity = parseFirstNumber(text);
  const unit = parseUnitAfterNumber(text);
  const itemName = extractItemName(original, materialName);

  return {
    status: 'parsed',
    action: 'add_material_item',
    itemName,
    quantity,
    unit,
    label: `Materiał: ${itemName}`,
  };
}

function parseLineItem(original: string, text: string): ParsedVoiceCommand | null {
  if (!hasAny(text, ['dopisz', 'dodaj'])) return null;

  const quantity = parseFirstNumber(text);
  if (!quantity) return null;

  const unit = parseUnitAfterNumber(text);
  const itemName = extractAfterNumber(original);

  if (!itemName) return null;

  return {
    status: 'parsed',
    action: 'add_line_item',
    itemName,
    quantity,
    unit,
    label: `Pozycja: ${itemName}`,
  };
}

function parseFirstNumber(text: string): number | undefined {
  const match = text.match(/(\d+(?:[,.]\d+)?)/);
  if (!match) return undefined;

  const value = Number(match[1].replace(',', '.'));
  return Number.isFinite(value) && value > 0 ? value : undefined;
}

function parseUnitAfterNumber(text: string): string | undefined {
  const match = text.match(/\d+(?:[,.]\d+)?\s+([a-z0-9]+)/);
  return match?.[1];
}

function extractAfterNumber(original: string): string {
  return original
    .replace(/^(dodaj|dopisz)\s+/i, '')
    .replace(/\d+(?:[,.]\d+)?\s+\S+\s*/i, '')
    .trim();
}

function extractItemName(original: string, fallback: string): string {
  const cleaned = original
    .replace(/^(dodaj|dopisz)\s+/i, '')
    .replace(/\d+(?:[,.]\d+)?\s+\S+/i, '')
    .trim();

  return cleaned || fallback;
}

function findFirst(text: string, values: string[]): string | null {
  return values.find((value) => text.includes(value)) ?? null;
}

function hasAny(text: string, values: string[]): boolean {
  return values.some((value) => text.includes(value));
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}
